import { Middleware } from '@reduxjs/toolkit';

import {
  continueProcessing,
  fetchCertainPaymentMethodSuccess,
  fetchPaymentInfoSuccess,
  initiateConfirmation,
  initiateProcessing,
  processPayment,
  processPaymentFailure,
  processPaymentSuccess,
  registerPayment,
  registrationPaymentFailure,
  registrationPaymentSuccess,
  selectPaymentInputs,
  setAccounts,
  setActiveScreen,
  setConfirmationFormData,
  setInputErrorMap,
  toggleProcessingLoading,
  toInitialPaymentInfoState,
} from '@cashier/cashier-data-access';
import {
  CashierMessages,
  DynamicScreens,
  FieldNames,
  IFRAME_FIELDS,
  PaymentActionError,
  PaymentActionResult,
  PaymentStates,
  PaymentTypes,
  RedirectContainers,
  ResultAction,
  ResultActionMethods,
  ResultActionTypes,
  WindowNames,
} from '@cashier/cashier-model';
import {
  AmountConverter,
  eventBus,
  EventFactory,
  ProviderWindowService,
} from '@cashier/cashier-shared';

import { AMOUNT_FIELD_NAME } from '../constants';
import {
  getCookie,
  isFinteqHubCardDeposit,
  PliromyService,
  QaToolKitOptions,
  shouldTokenize,
} from '../helpers';
import { selectProcessingAttributes } from '../selectors';

export const paymentProcessingMiddleware: Middleware =
  (store) => (next) => async (action) => {
    const {
      paymentMethods: {
        paymentMethod: {
          vendor,
          id: paymentMethodId,
          referenceId,
          source,
          hideAmount,
          preprocessor,
          redirectContainer,
        },
        registrationRequired,
      },
      cashierConfiguration: {
        merchantId,
        userId,
        sessionId,
        activeType,
        language,
        hostUrl,
        currency,
      },
      customerInformation: { bonusCode },
      accounts: { activeAccountOption },
    } = store.getState();

    // TODO delete after testing 2914
    const isHostedFieldEnabled =
      getCookie(QaToolKitOptions.enableHostedField) === 'true';

    const result = next(action);

    if (action.type === initiateProcessing.toString()) {
      if (isHostedFieldEnabled) {
        const paymentAttributes = selectProcessingAttributes(store.getState());

        const eventByType = {
          deposit: 'deposit_form_submit',
          withdrawal: 'withdraw_form_submit',
        };

        eventBus.emit(
          EventFactory.paymentInitiated(
            eventByType[activeType],
            paymentAttributes.amount,
            paymentMethodId,
            currency,
          ),
        );

        if (hideAmount) {
          paymentAttributes[AMOUNT_FIELD_NAME] = '0';
        }

        if (shouldTokenize(preprocessor)) {
          eventBus.emit(
            EventFactory.cashierMessage({
              task: CashierMessages.LoadPliromyTokenizer,
            }),
          );
          return;
        }

        const paymentInputs = selectPaymentInputs(store.getState());
        const iframesCollection = new Set<FieldNames>(IFRAME_FIELDS);
        const workingFields = Object.values(paymentInputs || {}).filter((el) =>
          iframesCollection.has(el?.name as FieldNames),
        );
        const iframesSchema = new Set(
          workingFields.map((field) => field.name as FieldNames),
        );

        if (iframesSchema.size > 0) {
          eventBus.emit(
            EventFactory.cashierMessage({
              task: CashierMessages.GetEncryptedFieldsData,
            }),
          );
        } else {
          store.dispatch(continueProcessing({ ...paymentAttributes }));
        }
      } else {
        // TODO delete after testing 2914
        const paymentAttributes = selectProcessingAttributes(store.getState());
        const currentPaymentMethod =
          store.getState().paymentMethods.paymentMethod;
        let preprocessedAttributes: Record<string, any> = {};

        const eventByType = {
          deposit: 'deposit_form_submit',
          withdrawal: 'withdraw_form_submit',
        };

        eventBus.emit(
          EventFactory.paymentInitiated(
            eventByType[activeType],
            paymentAttributes.amount,
            paymentMethodId,
            currency,
          ),
        );

        if (hideAmount) {
          paymentAttributes[AMOUNT_FIELD_NAME] = '0';
        }

        const callbackParameterBag = {
          userId,
          sessionId,
          merchantId,
          language,
          host: hostUrl,
          paymentMethodId,
        };

        store.dispatch(toInitialPaymentInfoState());

        if (shouldTokenize(currentPaymentMethod.preprocessor)) {
          try {
            const paymentInputs = selectPaymentInputs(store.getState());
            preprocessedAttributes =
              await PliromyService.getPreprocessedAttributes(
                paymentInputs,
                currentPaymentMethod,
                activeAccountOption,
              );
          } catch (error) {
            store.dispatch(
              processPaymentFailure({
                message: error,
                attributeErrorBag: null,
                errorDetails: { description: error },
                transactionId: error.transactionId,
              }),
            );
            return;
          }
        }

        if (registrationRequired) {
          store.dispatch(
            registerPayment({
              referenceId,
              type: activeType,
              walletId: userId,
              sessionId,
              merchantId,
              registration: {
                accountId: activeAccountOption?.value || null,
                methodId: paymentMethodId,
                attributes: {
                  ...paymentAttributes,
                  amount: AmountConverter.toNonBreakingString(
                    paymentAttributes.amount as string,
                  ),
                  bonusCode,
                  ...preprocessedAttributes,
                },
              },
            }),
          );
        } else {
          store.dispatch(
            processPayment({
              paymentMethodId,
              vendor,
              merchantId,
              paymentType: activeType,
              userId,
              sessionId,
              accountId: activeAccountOption?.value || null,
              paymentAttributes: {
                ...paymentAttributes,
                amount: AmountConverter.toNonBreakingString(
                  paymentAttributes.amount as string,
                ),
                bonusCode,
                ...preprocessedAttributes,
              },
              callbackParameterBag,
              referenceId,
              source,
            }),
          );
        }
      }
    }

    if (action.type === processPaymentSuccess.toString()) {
      const result: PaymentActionResult = action.payload;
      const { transactionId, status, paymentAttributes, resultAction } = result;

      if (paymentAttributes && paymentAttributes.length > 0) {
        store.dispatch(
          setConfirmationFormData({ transactionId, paymentAttributes }),
        );

        if (isFinteqHubCardDeposit(preprocessor)) {
          store.dispatch(toggleProcessingLoading(true));
          store.dispatch(initiateConfirmation());
          return;
        }

        store.dispatch(setActiveScreen(DynamicScreens.ConfirmationForm));
      } else {
        store.dispatch(
          fetchPaymentInfoSuccess({
            transactionId,
            state: status as PaymentStates,
          }),
        );

        if (!resultAction) {
          eventBus.emit(EventFactory.scrollTop());

          ProviderWindowService.close(WindowNames.Window);
        }
      }
    }

    if (action.type === processPaymentFailure.toString()) {
      if (activeType === PaymentTypes.Deposit) {
        const error: PaymentActionError = action.payload;
        const { description } = error.errorDetails || {};

        const paymentAttributes = selectProcessingAttributes(store.getState());

        eventBus.emit(
          EventFactory.depositError(
            paymentAttributes.amount,
            currency,
            paymentMethodId,
            description || '',
            error.transactionId,
            error?.errCode,
          ),
        );
      }
    }

    if (action.type === registrationPaymentSuccess.toString()) {
      const resultAction: ResultAction = action.payload;

      store.dispatch(setActiveScreen(DynamicScreens.ProcessingResult));

      const isRedirect = !!(
        ResultActionMethods.GET === resultAction.method &&
        ResultActionTypes.REDIRECT === resultAction.type &&
        resultAction.url
      );

      if (isRedirect) {
        ProviderWindowService.redirect(
          resultAction.url,
          redirectContainer === RedirectContainers.NEW_TAB,
        );
      }

      if (resultAction.type === ResultActionTypes.DISPLAY) {
        store.dispatch(setActiveScreen(DynamicScreens.FinalScreen));
      }
    }

    if (
      [registrationPaymentFailure.type, processPaymentFailure.type].includes(
        action.type,
      )
    ) {
      ProviderWindowService.close(WindowNames.Window);

      const error: PaymentActionError = action.payload;
      if (error.attributeErrorBag) {
        store.dispatch(setInputErrorMap(error.attributeErrorBag));
      }
    }

    if (action.type === fetchCertainPaymentMethodSuccess.toString()) {
      const { method } = action.payload;
      store.dispatch(setAccounts(action.payload));

      if (
        !method.description &&
        !method.inputs.some((input) => input.required)
      ) {
        store.dispatch(initiateProcessing());
      }
    }

    return result;
  };
